/**
 * <LangProvider> — React tree wrapper that exposes the current language +
 * a setter to descendants. Wraps I18nextProvider and Cloudscape's own
 * <I18nProvider> so app strings AND component strings share one source of
 * truth: whatever the user picked in <LangSwitcher>.
 *
 * Cloudscape gotcha: it only ships built-in messages for a handful of
 * languages (en, de, fr, ja, ko, pt-BR, zh-CN). RU and KK are NOT among
 * them. For now we let Cloudscape fall back to English component strings
 * (pagination "Next page", form validation "Value is required", etc.) —
 * a graceful degradation. Full Cloudscape translation is a Phase-2 add.
 */
import I18nProvider from '@cloudscape-design/components/i18n';
import enMessages from '@cloudscape-design/components/i18n/messages/all.en.json' with { type: 'json' };
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { DEFAULT_LANG, LS_KEY, type Lang, isLang } from './config.js';
import { getI18n, setLanguage } from './i18n.js';

function readInitialLang(): Lang {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(LS_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* localStorage unavailable */
  }
  const navigatorLang = window.navigator.language?.slice(0, 2).toLowerCase();
  if (navigatorLang === 'ru') return 'ru';
  if (navigatorLang === 'kk') return 'kk';
  return DEFAULT_LANG;
}

export interface LangProviderProps {
  children: ReactNode;
}

export function LangProvider({ children }: LangProviderProps): ReactNode {
  const [lang, setLangState] = useState<Lang>(readInitialLang);
  const i18n = useMemo(() => getI18n(lang), [lang]);

  // Keep i18next's active language in sync with our React state. Also update
  // <html lang> for screen readers + browser spellcheck.
  useEffect(() => {
    void setLanguage(lang);
  }, [lang]);

  // Broadcast lang changes to other tabs — a common admin pattern is
  // several tabs open on different tenants; switching language once should
  // apply everywhere.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && isLang(e.newValue) && e.newValue !== lang) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [lang]);

  // Context expose the setter so <LangSwitcher> can call it. Rendering
  // through an inner component keeps the switcher usable without prop-
  // drilling.
  return (
    <LangContext.Provider value={{ lang, setLang: setLangState }}>
      <I18nextProvider i18n={i18n}>
        <I18nProvider messages={[enMessages]} locale={lang}>
          {children}
        </I18nProvider>
      </I18nextProvider>
    </LangContext.Provider>
  );
}

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

import { createContext, useContext } from 'react';

const LangContext = createContext<LangContextValue | null>(null);

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside <LangProvider>');
  return ctx;
}
